import { useState } from 'react';
import { Editor, useEditorState } from '@tiptap/react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
} from 'lucide-react';

interface EditorToolbarProps {
  editor: Editor | null;
}

export function EditorToolbar ({ editor }: EditorToolbarProps) {
    // Subscribe to editor state changes so toolbar buttons update immediately
    const editorState = useEditorState({
        editor,
        selector: ({ editor: e }) => ({
            isBold: e?.isActive('bold') ?? false,
            isItalic: e?.isActive('italic') ?? false,
            isUnderline: e?.isActive('underline') ?? false,
            isStrike: e?.isActive('strike') ?? false,
            isBulletList: e?.isActive('bulletList') ?? false,
            isOrderedList: e?.isActive('orderedList') ?? false,
            isBlockquote: e?.isActive('blockquote') ?? false,
            isLink: e?.isActive('link') ?? false,
        }),
    });

    if (!editor || !editorState) return null;

    return (
        <div className="flex items-center gap-1 p-2 border-b border-white/[0.06]">
            {/* Text Styling Group */}
            <ToolbarGroup>
                {/* Bold */}
                <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                active={editorState.isBold}
                title="Bold (Ctrl+B)"
                >
                    <Bold size={18} />
                </ToolbarButton>
                {/* Italic */}
                <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                active={editorState.isItalic}
                title="Italic (Ctrl+I)"
                >
                    <Italic size={18} />
                </ToolbarButton>
                {/* Underline */}
                <ToolbarButton
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                active={editorState.isUnderline}
                title="Underline (Ctrl+U)"
                >
                    <Underline size={18} />
                </ToolbarButton>
                {/* Strikethrough */}
                <ToolbarButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                active={editorState.isStrike}
                title="Strikethrough"
                >
                    <Strikethrough size={18} />
                </ToolbarButton>
            </ToolbarGroup>
            {/* List & Quote Group */}
            <ToolbarGroup>
                {/* Bullet List */}
                <ToolbarButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                active={editorState.isBulletList}
                title="Bullet List"
                >
                    <List size={18} />
                </ToolbarButton>
                {/* Ordered List */}
                <ToolbarButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                active={editorState.isOrderedList}
                title="Numbered List"
                >
                    <ListOrdered size={18} />
                </ToolbarButton>
                {/* Quote */}
                <ToolbarButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                active={editorState.isBlockquote}
                title="Quote"
                >
                    <Quote size={18} />
                </ToolbarButton>
            </ToolbarGroup>

            <Separator />

            {/* Link Group */}
            <ToolbarGroup>
                <LinkButton editor={editor} active={editorState.isLink} />
            </ToolbarGroup>

        </div>
    )
}

function ToolbarButton({
  onClick,
  active,
  title,
  children
}: {
  onClick: () => void;
  active: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-[#A38BFF]/20 text-[#A38BFF]'
          : 'text-[#A7B0C8] hover:text-[#F5F7FF] hover:bg-white/[0.04]'
      }`}
    >
      {children}
    </button>
  );
}

function LinkButton({ editor, active }: { editor: Editor; active: boolean }) {
  const [showDialog, setShowDialog] = useState(false);
  const [url, setUrl] = useState('');

  const handleSubmit = () => {
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setShowDialog(false);
    setUrl('');
  };

  return (
    <>
      <ToolbarButton
        onClick={() => {
          const previousUrl = editor.getAttributes('link').href;
          setUrl(previousUrl || '');
          setShowDialog(true);
        }}
        active={active}
        title="Insert Link"
      >
        <LinkIcon size={18} />
      </ToolbarButton>

      {showDialog && (
        <LinkDialog
          url={url}
          onUrlChange={setUrl}
          onSubmit={handleSubmit}
          onCancel={() => setShowDialog(false)}
        />
      )}
    </>
  );
}

function Separator() {
  return <div className="w-px h-5 bg-white/[0.08] mx-1" />;
}

function ToolbarGroup({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-0.5">{children}</div>;
}

function LinkDialog({
  url,
  onUrlChange,
  onSubmit,
  onCancel,
}: {
  url: string;
  onUrlChange: (url: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onCancel}>
      <div
        className="bg-[#141821] rounded-xl p-4 w-80 border border-white/[0.08] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-[#F5F7FF] mb-3">Insert Link</h3>
        <input
          type="url"
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          placeholder="https://example.com"
          className="w-full bg-[#0B0D10] rounded-lg px-3 py-2 text-sm text-[#F5F7FF] placeholder-[#A7B0C8] outline-none border border-white/[0.08] focus:border-[#A38BFF]/50 mb-3"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSubmit();
            } else if (e.key === 'Escape') {
              onCancel();
            }
          }}
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-3 py-1.5 text-sm bg-[#A38BFF] text-[#0B0D10] rounded-lg font-medium hover:bg-[#A38BFF]/90 transition-colors"
          >
            {url ? 'Apply' : 'Remove Link'}
          </button>
        </div>
      </div>
    </div>
  );
}